import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { lastValueFrom } from 'rxjs';
import { WebRtcService } from '../../services/webrtc.service';
import { ProviderService } from '../../services/provider.service';

@Component({
  selector: 'app-webrtc-connection-window',
  standalone: true,
  imports: [CommonModule, ToastModule],
  templateUrl: './webrtc-connection-window.component.html',
  styleUrl: './webrtc-connection-window.component.scss',
  providers: [MessageService]
})
export class WebRtcConnectionWindowComponent implements AfterViewInit, OnDestroy {
  @ViewChild('remoteCanvas', { static: true }) remoteCanvas!: ElementRef<HTMLCanvasElement>;
  sessionId?: string;
  private connected = false;
  private activeResolution: { width: number; height: number } = { width: 1920, height: 1080 };
  connectionError?: string;

  constructor(
    private webRtcService: WebRtcService,
    private providerService: ProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  async ngAfterViewInit(): Promise<void> {
    try {
      const sessionId = this.route.snapshot.paramMap.get('sessionId') ?? '';
      const width = parseInt(this.route.snapshot.queryParamMap.get('width') ?? '1920');
      const height = parseInt(this.route.snapshot.queryParamMap.get('height') ?? '1080');

      if (!sessionId) {
        throw new Error('Session ID is missing');
      }

      this.sessionId = sessionId;
      this.activeResolution = {
        width: Number.isFinite(width) && width > 0 ? width : 1920,
        height: Number.isFinite(height) && height > 0 ? height : 1080
      };
      const credentials = await this.getOrLoadCredentials(sessionId);
      await this.webRtcService.connect(
        sessionId,
        credentials,
        this.activeResolution,
        this.remoteCanvas.nativeElement
      );
      this.connected = true;
      this.remoteCanvas.nativeElement.focus();
    }
    catch (error: any) {
      this.connectionError = error?.message || 'Unknown error';
      console.error('WebRTC initialization failed', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Connection Error',
        detail: 'Failed to initialize WebRTC connection: ' + this.connectionError,
        life: 6000
      });
      return;
    }
  }

  disconnect(): void {
    this.connected = false;
    this.webRtcService.disconnect();
    this.router.navigate(['/app/pending-sessions']);
  }

  @HostListener('window:keydown.escape')
  onEscape(): void {
    this.disconnect();
  }

  ngOnDestroy(): void {
    this.connected = false;
    this.webRtcService.disconnect();
  }

  onMouseMove(event: MouseEvent): void {
    this.sendMouseEvent('move', event);
  }

  onMouseDown(event: MouseEvent): void {
    this.sendMouseEvent('down', event);
  }

  onMouseUp(event: MouseEvent): void {
    this.sendMouseEvent('up', event);
  }

  onWheel(event: WheelEvent): void {
    if (!this.connected) {
      return;
    }
    const normalized = this.normalizePointer(event.clientX, event.clientY);
    this.webRtcService.sendInputEvent({
      type: 'wheel',
      deltaX: event.deltaX,
      deltaY: event.deltaY,
      ...normalized
    });
    event.preventDefault();
  }

  onKeyDown(event: KeyboardEvent): void {
    this.sendKeyboardEvent('down', event);
  }

  onKeyUp(event: KeyboardEvent): void {
    this.sendKeyboardEvent('up', event);
  }

  private sendMouseEvent(action: 'move' | 'down' | 'up', event: MouseEvent): void {
    if (!this.connected) {
      return;
    }
    const normalized = this.normalizePointer(event.clientX, event.clientY);
    this.webRtcService.sendInputEvent({
      type: 'mouse',
      action,
      button: event.button,
      ...normalized
    });
    event.preventDefault();
  }

  private sendKeyboardEvent(action: 'down' | 'up', event: KeyboardEvent): void {
    if (!this.connected) {
      return;
    }
    this.webRtcService.sendInputEvent({
      type: 'keyboard',
      action,
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      altKey: event.altKey,
      shiftKey: event.shiftKey,
      metaKey: event.metaKey
    });
    event.preventDefault();
  }

  private normalizePointer(clientX: number, clientY: number): { x: number; y: number; clientWidth: number; clientHeight: number } {
    const rect = this.remoteCanvas.nativeElement.getBoundingClientRect();
    const displayWidth = Math.max(1, rect.width);
    const displayHeight = Math.max(1, rect.height);
    const decodedWidth = this.remoteCanvas.nativeElement.width;
    const decodedHeight = this.remoteCanvas.nativeElement.height;
    const streamWidth = Math.max(1, decodedWidth || this.activeResolution.width);
    const streamHeight = Math.max(1, decodedHeight || this.activeResolution.height);
    const streamAspect = streamWidth / streamHeight;
    const displayAspect = displayWidth / displayHeight;

    let renderedWidth = displayWidth;
    let renderedHeight = displayHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (displayAspect > streamAspect) {
      renderedHeight = displayHeight;
      renderedWidth = renderedHeight * streamAspect;
      offsetX = (displayWidth - renderedWidth) / 2;
    } else if (displayAspect < streamAspect) {
      renderedWidth = displayWidth;
      renderedHeight = renderedWidth / streamAspect;
      offsetY = (displayHeight - renderedHeight) / 2;
    }

    const renderedX = clientX - rect.left - offsetX;
    const renderedY = clientY - rect.top - offsetY;
    const x = Math.min(Math.max(renderedX / Math.max(1, renderedWidth), 0), 1);
    const y = Math.min(Math.max(renderedY / Math.max(1, renderedHeight), 0), 1);

    return {
      x,
      y,
      clientWidth: streamWidth,
      clientHeight: streamHeight
    };
  }

  private async getOrLoadCredentials(sessionId: string) {
    try {
      return this.webRtcService.getSessionCredentials(sessionId);
    } catch {
      const credentials = await lastValueFrom(this.providerService.pendingSessionsService.getMachineCredentials(sessionId));
      if (!credentials) {
        throw new Error('Missing WebRTC session credentials.');
      }
      this.webRtcService.setSessionCredentials(sessionId, credentials);
      return credentials;
    }
  }
}
